/**
 * useBlogFeed — paginated WLB blog posts (GetFeeds with method "Blogs").
 *
 * Mirrors the shape of useUserPosts so a post list rendered from either hook
 * behaves the same, and keeps the like/save mutations on the API instead of
 * local-only toggles.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import feedService from '../api/services/feedService';
import { getUserFacingErrorMessage } from '../utils/apiErrorHandler';
import { useTranslation } from '../i18n/useTranslation';
import type { FeedPost } from '../types/post';

export interface UseBlogFeedResult {
  posts: FeedPost[];
  page: number;
  hasNextPage: boolean;
  loading: boolean;
  isFetchingMore: boolean;
  error: string;
  loaded: boolean;
  fetchNextPage: () => void;
  refresh: () => void;
  /** Optimistic like toggle, rolled back when the API rejects it. */
  likePost: (postId: string) => Promise<void>;
  patchPost: (postId: string, patch: Partial<FeedPost>) => void;
  removePost: (postId: string) => void;
}

export const useBlogFeed = (): UseBlogFeedResult => {
  const { t } = useTranslation();
  const [posts, setPosts] = useState<FeedPost[]>([]);
  const [page, setPage] = useState(1);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isFetchingMore, setIsFetchingMore] = useState(false);
  const [error, setError] = useState('');
  const [loaded, setLoaded] = useState(false);

  const inFlight = useRef<Set<number>>(new Set());

  const load = useCallback(async (targetPage: number) => {
    if (inFlight.current.has(targetPage)) return;
    inFlight.current.add(targetPage);

    if (targetPage === 1) setLoading(true);
    else setIsFetchingMore(true);
    setError('');

    try {
      const result = await feedService.getBlogFeed(targetPage);
      setPosts(previous => {
        if (targetPage === 1) return result.data;
        const seen = new Set(previous.map(post => post.id));
        return [...previous, ...result.data.filter(post => !seen.has(post.id))];
      });
      setPage(targetPage);
      setHasNextPage(result.hasNextPage);
      setLoaded(true);
    } catch (caught) {
      // `loaded` is deliberately not set here: a first page that failed has
      // answered nothing, and the screen must show the failure, not "no posts".
      setError(getUserFacingErrorMessage(caught, t('common.errors.generic')));
      if (targetPage === 1) setPosts([]);
      setHasNextPage(false);
    } finally {
      inFlight.current.delete(targetPage);
      setLoading(false);
      setIsFetchingMore(false);
    }
  }, [t]);

  useEffect(() => {
    load(1).catch(() => undefined);
  }, [load]);

  const fetchNextPage = useCallback(() => {
    if (loading || isFetchingMore || !hasNextPage) return;
    load(page + 1).catch(() => undefined);
  }, [hasNextPage, isFetchingMore, load, loading, page]);

  const refresh = useCallback(() => {
    load(1).catch(() => undefined);
  }, [load]);

  const patchPost = useCallback((postId: string, patch: Partial<FeedPost>) => {
    setPosts(previous =>
      previous.map(post => (post.id === postId ? { ...post, ...patch } : post)),
    );
  }, []);

  const removePost = useCallback((postId: string) => {
    setPosts(previous => previous.filter(post => post.id !== postId));
  }, []);

  const likePost = useCallback(
    async (postId: string) => {
      let currentlyLiked = false;
      const toggle = (post: FeedPost): FeedPost => {
        currentlyLiked = Boolean(post.liked);
        const liked = !post.liked;
        return {
          ...post,
          liked,
          likes: liked ? (post.likes || 0) + 1 : Math.max(0, (post.likes || 0) - 1),
        };
      };
      const apply = () =>
        setPosts(previous =>
          previous.map(post => (post.id === postId ? toggle(post) : post)),
        );

      apply();
      try {
        const result = await feedService.likePost(postId, { currentlyLiked });
        if (!result.success) apply();
      } catch {
        apply();
      }
    },
    [],
  );

  return {
    posts,
    page,
    hasNextPage,
    loading,
    isFetchingMore,
    error,
    loaded,
    fetchNextPage,
    refresh,
    likePost,
    patchPost,
    removePost,
  };
};

export default useBlogFeed;
